'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchBanners } from '@eliadmin/core/src/api/general/general-api';
import { Button } from '@eliadmin/core/src/components/ui/button';
import type { CarouselProps } from '@eliadmin/core/src/components/ui/carousel';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore, type AvailableLocales } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery } from '@tanstack/react-query';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { useRouter } from 'next/navigation';

function pageUrlByLocale(locale: string): string {
  const urlMap: { [key: string]: string } = {
    sk: 'video-navody',
    cs: 'video-navody',
    en: 'video-instructions',
    it: 'istruzioni-video',
  };
  return `/${urlMap[locale] || urlMap['cs']}`;
}

export type BannerSliderProps = {
  banners?: Awaited<ReturnType<typeof fetchBanners>>['data'];
  locale?: AvailableLocales;
  className?: {
    main?: string;
    banner?: string;
  };

  carouselPropsMobile?: Omit<CarouselProps, 'slides'>;
  carouselPropsDesktop?: Omit<CarouselProps, 'slides'>;
};

export function BannerSlider(props: BannerSliderProps) {
  const currentLocale = useLocaleStore(s => s.currentLocale);
  const locale = props.locale ?? currentLocale;
  const { t } = useTranslations();
  const router = useRouter();

  const { data: fetchedBanners } = useQuery({
    queryKey: [CacheKey.BANNERS, 'slider'],
    queryFn: () =>
      fetchBanners({ locale, query: { where: { type: { equals: 'slider' } } } }),
    enabled: !props.banners,
  });
  const banners = props.banners ?? fetchedBanners?.data ?? [];

  const handleBannerClick = (url: string) => {
    if (url && url !== '#') {
      router.push(url);
    }
  };

  const handleButtonClick = (url: string) => {
    if (url && url !== '#') {
      router.push(url);
    }
  };

  const desktopSlides = banners
    .filter(i => i.types?.includes('desktop') && i.image)
    .map((item, i) => (
      <div
        key={i}
        className="relative max-h-[400px] cursor-pointer"
        onClick={() => handleBannerClick(item.url || '#')}
      >
        <Image
          priority={i === 0}
          src={item.image.url ?? ''}
          alt={resolveImageAlt(item?.image, item?.name)}
          width={0}
          height={0}
          quality={90}
          sizes="100vw"
          className={cn(
            'h-full max-h-[400px] min-h-[300px] w-full object-cover object-left-top',
            'ea-banner-slider__banner',
            'ea-banner-slider__banner--desktop',
            props.className?.banner,
          )}
        />
        {item.image.name === 'bg.jpg' && (
          <div className="z-1 absolute top-[14vh] flex w-full flex-col gap-8 pl-[4vw] lg:top-[15vh] xl:top-[20vh]">
            <h2 className="z-1 outlined-text max-w-[500px] text-[40px] leading-[48px] text-white">
              {t('text1764')}
            </h2>
            <Button
              className="w-auto max-w-min whitespace-nowrap bg-white text-primary hover:bg-primary hover:text-white"
              onClick={e => {
                e.stopPropagation();
                handleButtonClick(item.url || '#');
              }}
            >
              {t('www.KORCHEM.cz')}
            </Button>
          </div>
        )}
      </div>
    ));

  const mobileSlides = banners
    .filter(i => i.types?.includes('mobile') && i.image)
    .map((item, i) => (
      <div
        key={i}
        className="relative cursor-pointer"
        onClick={() => handleBannerClick(item.url || '#')}
      >
        <Image
          src={item.image.url ?? ''}
          alt={resolveImageAlt(item?.image, item?.name)}
          priority={i === 0}
          fetchPriority={i === 0 ? 'high' : undefined}
          width={0}
          height={0}
          quality={90}
          sizes="100vw"
          className={cn(
            'h-full min-h-[300px] w-full object-cover object-left-top',
            'ea-banner-slider__banner',
            'ea-banner-slider__banner--mobile',
            props.className?.banner,
          )}
        />
        {item.image.name === 'bg.jpg' && (
          <div className="z-1 absolute top-[14vh] flex w-full flex-col gap-6 pl-[6vw] sm:pl-[4vw]">
            <h2 className="z-1 outlined-text max-w-[350px] text-3xl text-white">{t('text1764')}</h2>
            <Button
              className="w-auto max-w-min whitespace-nowrap bg-white text-primary hover:bg-primary hover:text-white"
              onClick={e => {
                e.stopPropagation();
                handleButtonClick(item.url || '#');
              }}
            >
              {t('www.KORCHEM.cz')}
            </Button>
          </div>
        )}
      </div>
    ));

  if (!desktopSlides.length && !mobileSlides.length) return null;

  return (
    <>
      <div
        className={cn(
          'hidden max-h-[400px] w-full items-center justify-center md:flex',
          'ea-banner-slider',
          'ea-banner-slider__desktop',
          props.className?.main,
        )}
      >
        <div className="xl:max-w-[84vw]">
          <Carousel
            {...props.carouselPropsDesktop}
            slides={desktopSlides}
            enableLoop
            enableInterval
          />
        </div>
      </div>
      <div
        className={cn(
          'flex w-full max-w-screen-xl md:hidden',
          'ea-banner-slider',
          'ea-banner-slider__mobile',
          props.className?.main,
        )}
      >
        <Carousel {...props.carouselPropsMobile} slides={mobileSlides} enableLoop enableInterval />
      </div>
    </>
  );
}
